/**
 * +prof: Foraging and Mining (docs/design/professions.md): skills, WoW-style skill-ups, character
 * XP per gather, the yields into the Altar Forge wallet, and the server-side gather op.
 *
 * `applyGather` is the `fieldGather` intent: the client's sim says the hero finished gathering a
 * node; the op checks it against the map (the node exists, the hero stands near it and on that map),
 * the skill requirement, a per-node floor (a node can't be gathered again faster than its respawn)
 * and a server-clock token bucket, then rolls the skill-up and the yield on the profile seed (the
 * server's online) and pays XP, gold and resources. Pure: time only comes in as `nowMs`.
 */
import { gridIdleTotals } from './grid.compile'; // +grid
import type { GatherNodeDef, GatherProfession } from '@keystone/sim';
import { type GatherColor, type GatherNodeKind, GATHER_XP_UNITS, PROF_SKILL_CAP, SKILL_UP, gatherColor, gatherKindAt, gatherNodeKind, gatherTier, getContentBundle } from '@keystone/content';
import { credit } from './forge.wallet';
import { type FieldOp, fieldOpGrant, fieldOpSettle, fieldOpStart } from './field';
import { killXp } from './leveling';
import { profileRng } from './profile';
import type { DungeonDef } from '@keystone/sim';
import type { HeroCharacter, Profile, ProfessionState, ResourceBundle, ScaleGrade } from './types';

/** Server-clock gather budget: refills `perMin` a minute up to `burst` (a channel is ≥ 1.6 s, plus the walk). */
export const GATHER_RATE = { perMin: 10, burst: 10 } as const;
/** A gather may be reported this far (yd) from its node (the hero stands within 3.5; latency slack). */
export const GATHER_REPORT_YD = 8;
/** A node can't pay again sooner than this share of its shortest respawn (server clock). */
export const GATHER_NODE_FLOOR = 0.9;
const GATHER_SALT = 0x6a7e_57ed;

export function newProfessionState(): ProfessionState {
  return { skills: { foraging: 1, mining: 1 }, nodeAt: {}, budget: { lastMs: null, tokens: GATHER_RATE.burst }, gathers: 0, byKind: {} };
}

/** The hero's profession state (a fresh one when the save predates v10 or the hero never gathered). */
export function professionsOf(hero: Pick<HeroCharacter, 'prof'> | null | undefined): ProfessionState {
  const p = hero?.prof;
  if (!p) return newProfessionState();
  return { ...newProfessionState(), ...p, skills: { ...newProfessionState().skills, ...p.skills } };
}

export const skillOf = (hero: Pick<HeroCharacter, 'prof'> | null | undefined, prof: GatherProfession): number => professionsOf(hero).skills[prof];

/** Character XP for a gather of `tier` at `skill` by a hero of `heroLevel` (level-gap penalty, then the colour). */
export function gatherXp(tier: number, skill: number, heroLevel: number): number {
  const t = gatherTier(tier);
  const units = GATHER_XP_UNITS[gatherColor(skill, t.req)];
  return units > 0 ? Math.max(1, Math.round(units * killXp(t.level, heroLevel))) : 0;
}

/** One paid line of a gather (for pops and the toast). */
export interface GatherLine {
  resource: 'gold' | 'fangsteel' | 'scale' | 'venom' | 'moltskin';
  amount: number;
  grade?: ScaleGrade;
  rare?: boolean;
}

export interface GatherRoll {
  /** Skill points gained (0 = none). */
  skillUp: number;
  /** "Rich vein" / "Lush patch": everything the gather paid is doubled. */
  bonus: boolean;
  lines: GatherLine[];
  bundle: ResourceBundle;
}

/**
 * Roll one gather (pure given the rng). Draw order (part of the save contract): the skill-up, the
 * bonus, then two draws (chance, amount) per yield line, always, so a later line never shifts.
 */
export function rollGather(rng: { next(): number }, kind: GatherNodeKind, skill: number, skillUpMult = 1): GatherRoll {
  const t = gatherTier(kind.tier);
  const su = SKILL_UP[gatherColor(skill, t.req)];
  const up = rng.next() < Math.min(1, su.chance * skillUpMult) ? su.points : 0; // +grid: Craftlore stars raise the chance
  const bonus = rng.next() < t.bonusChance;
  const lines: GatherLine[] = [];
  kind.yields.forEach((y) => {
    const hit = rng.next() < y.chance;
    let amount = y.min + Math.floor(rng.next() * (y.max - y.min + 1));
    if (!hit || amount <= 0) return;
    if (bonus) amount *= 2;
    lines.push({ resource: y.resource, amount, ...(y.grade ? { grade: y.grade } : {}), ...(y.rare ? { rare: true } : {}) });
  });
  const bundle: ResourceBundle = {};
  for (const l of lines) {
    if (l.resource === 'scale') {
      const g = l.grade ?? 'ashen';
      bundle.scales = { ...bundle.scales, [g]: (bundle.scales?.[g] ?? 0) + l.amount };
    } else bundle[l.resource] = (bundle[l.resource] ?? 0) + l.amount;
  }
  return { skillUp: up, bonus, lines, bundle };
}

/** Expected yield per gather of a kind (docs / harness). */
export function expectedGather(kind: GatherNodeKind): ResourceBundle & { goldPerGather: number } {
  const t = gatherTier(kind.tier);
  const out: ResourceBundle = {};
  let gold = 0;
  kind.yields.forEach((y) => {
    const amt = y.chance * ((y.min + y.max) / 2) * (1 + t.bonusChance);
    if (y.resource === 'gold') gold += amt;
    else if (y.resource === 'scale') out.scales = { ...out.scales, [y.grade ?? 'ashen']: (out.scales?.[y.grade ?? 'ashen'] ?? 0) + amt };
    else out[y.resource] = (out[y.resource] ?? 0) + amt;
  });
  return { ...out, goldPerGather: gold };
}

/** Refill the gather budget to `nowMs` (time running backwards refills nothing). */
export function refillGather(b: ProfessionState['budget'], nowMs: number): ProfessionState['budget'] {
  if (b.lastMs === null) return { lastMs: nowMs, tokens: Math.min(GATHER_RATE.burst, Math.max(0, b.tokens)) };
  const dt = Math.max(0, nowMs - b.lastMs);
  return { lastMs: Math.max(b.lastMs, nowMs), tokens: Math.min(GATHER_RATE.burst, Math.max(0, b.tokens) + (dt / 60_000) * GATHER_RATE.perMin) };
}

/** What a gather paid (the intent result's `gather`). */
export interface GatherResult {
  nodeId: string;
  kind: string;
  name: string;
  profession: GatherProfession;
  tier: number;
  color: GatherColor;
  skillBefore: number;
  skillAfter: number;
  xp: number;
  bonus: boolean;
  lines: GatherLine[];
}

const mapDef = (id: string, maps?: readonly DungeonDef[]): DungeonDef | undefined => (maps ?? getContentBundle().dungeons).find((d) => d.id === id);

/** The node `nodeId` on `mapId` (null when either is unknown). */
export function gatherNodeDef(mapId: string, nodeId: string, maps?: readonly DungeonDef[]): GatherNodeDef | null {
  return mapDef(mapId, maps)?.field?.gatherNodes?.find((n) => n.id === nodeId) ?? null;
}

/**
 * The `fieldGather` intent. Fails (nothing paid) when the node or map is unknown, the hero is not on
 * that map or not near the node, the skill is below the node's requirement, the node was paid too
 * recently, or the budget is empty. Rolls (profile seed).
 */
export function applyGather(profile: Profile, mapId: string, nodeId: string, pos: { x: number; y: number }, nowMs: number, maps?: readonly DungeonDef[]): FieldOp<{ gather: GatherResult }> {
  if (!Number.isFinite(nowMs)) return { ok: false, reason: 'no clock' };
  const node = gatherNodeDef(mapId, nodeId, maps);
  const kind = node ? gatherNodeKind(node.kind) : undefined;
  if (!node || !kind) return { ok: false, reason: 'Unknown node' };
  if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return { ok: false, reason: 'No position' };
  if (Math.hypot(pos.x - node.pos.x, pos.y - node.pos.y) > GATHER_REPORT_YD) return { ok: false, reason: 'Too far from the node' };
  const s = fieldOpStart(profile);
  if ('reason' in s) return s;
  const { p, hero, gain } = s;
  if (hero.mapId !== mapId) return { ok: false, reason: 'Not on that map' };
  const st = professionsOf(hero);
  const skill = st.skills[node.profession];
  if (skill < node.req) return { ok: false, reason: `Requires ${node.profession === 'mining' ? 'Mining' : 'Foraging'} ${node.req}` };
  const key = `${mapId}:${node.id}`;
  const last = st.nodeAt[key];
  if (last !== undefined && nowMs - last < node.respawnMs[0] * GATHER_NODE_FLOOR) return { ok: false, reason: 'That node is still depleted' };
  const budget = refillGather(st.budget, nowMs);
  if (budget.tokens < 1) return { ok: false, reason: 'Gathering too fast' };
  budget.tokens -= 1;
  st.budget = budget;
  // Forget nodes whose floor has long passed (keeps the save small).
  for (const [k, at] of Object.entries(st.nodeAt)) if (nowMs - at > 600_000) delete st.nodeAt[k];
  st.nodeAt[key] = nowMs;
  const rng = profileRng(p, (GATHER_SALT ^ Math.imul(st.gathers + 1, 0x9e3779b1)) >>> 0);
  const roll = rollGather(rng, kind, skill, 1 + (gridIdleTotals(p.characters[hero.classId]?.grid?.nodes ?? []).profSkillUp ?? 0)); // +grid
  st.gathers += 1;
  st.byKind[kind.id] = (st.byKind[kind.id] ?? 0) + 1;
  const after = Math.min(PROF_SKILL_CAP, skill + roll.skillUp);
  st.skills = { ...st.skills, [node.profession]: after };
  hero.prof = st;
  const level = p.characters[hero.classId]!.hero.level;
  const xp = gatherXp(node.tier, skill, level);
  const { gold = 0, ...mats } = roll.bundle;
  credit(p, mats);
  fieldOpGrant(p, hero, gain, xp, gold);
  hero.pos = { x: pos.x, y: pos.y };
  fieldOpSettle(p, hero, gain);
  return {
    ok: true,
    profile: p,
    ...gain,
    gather: { nodeId: node.id, kind: kind.id, name: kind.name, profession: node.profession, tier: node.tier, color: gatherColor(skill, node.req), skillBefore: skill, skillAfter: after, xp: gain.xp, bonus: roll.bonus, lines: roll.lines },
  };
}

/** The next tier a skill unlocks in a profession (null at the top). */
export function nextUnlock(prof: GatherProfession, skill: number): { tier: number; req: number; name: string } | null {
  for (let t = 1; t <= 4; t++) {
    const req = gatherTier(t).req;
    if (skill < req) return { tier: t, req, name: gatherKindAt(prof, t).name };
  }
  return null;
}

